import * as React from 'react';
import * as classNames from 'classnames';
import isFunction = require('lodash/isFunction');

import './style.sass';

const ESCAPE_KEY: number = 27;

interface IDropdownProps {
  label?: string;
  value?: string;
  disabled?: boolean;
  options: Array<IDropdownOption>;
  placeholder?: string;
  error?: string;
  onChange?: (value: IDropdownOption) => void;
  customClassName?: string;
}

interface IDropdownState {
  value?: string;
  opened?: boolean;
}

export interface IDropdownOption {
  value: string;
  label: string | React.ReactElement<any>;
}

export class Dropdown extends React.Component<IDropdownProps, IDropdownState> {

  public static defaultProps: IDropdownProps = {
    label: '',
    value: '',
    placeholder: '',
    error: '',
    disabled: false,
    options: [],
    customClassName: '',
    onChange: () => undefined,
  };

  private ref: HTMLElement;

  public constructor(props: IDropdownProps) {
    super(props);

    this.state = {
      opened: false,
      value: this.props.value,
    };
  }

  public componentDidMount(): void {
    document.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('keydown', this.onEscape);
  }

  public componentWillUnmount(): void {
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('keydown', this.onEscape);
  }

  public render(): React.ReactElement<{}> {
    const {opened} = this.state;
    const {options, label, error, customClassName, disabled, value} = this.props;
    const dropdownClassName = 'dropdown-component__wrapper';

    return (
      <div
        ref={this.setRef}
        className={classNames(dropdownClassName, customClassName, {
          'is-open': opened,
          [`${dropdownClassName}_disabled`]: disabled,
        })}
        onClick={this.toggleList}
      >
        <span className={'dropdown-component__label'}>{label}</span>
        <span className={classNames('dropdown-component__value', {'dropdown-component__error': !!error})}>
          {(options.find(option => option.value === value) || {label: ''}).label}
        </span>
        <ul className={'dropdown-component__list'}>
          {
            options.map((option: IDropdownOption) => (
              <li className={classNames('dropdown-component__item', {'is-active': option.value === value})}
                  onClick={(event: React.MouseEvent<HTMLLIElement>) => this.selectValue(event, option)}
                  key={option.value}
                  value={option.value}>{option.label}</li>
            ))
          }
        </ul>
      </div>
    );
  }

  private selectValue(event: React.MouseEvent<HTMLLIElement>, option: IDropdownOption): void {
    if (!this.state.opened) {
      return;
    }
    event.stopPropagation();
    this.setState({
      value: option.value,
      opened: false,
    });
    this.props.onChange(option);
  }

  private toggleList = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    this.setState({opened: !this.state.opened});
  };

  private onEscape = (event): void => {
    this.state.opened
      && event.keyCode === ESCAPE_KEY
      && this.close();
  }

  private onMouseDown = (event): void => {
    this.state.opened
      && !this.hasElement(event.target)
      && this.close();
  }

  private hasElement = (target: HTMLElement): boolean => {
    return isFunction(this.ref.contains) && this.ref.contains(target);
  }

  private close = (): void => {
    this.setState({opened: false});
  }

  private setRef = (node: HTMLElement): void => {
    this.ref = node;
  }
}
